import React, { useContext, useState } from 'react';
import { Row, Nav } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { TfiArrowCircleLeft, TfiArrowCircleRight, TfiClose, TfiCheck } from 'react-icons/tfi';
import PropTypes from 'prop-types';
import { CurrencyContext } from '../../utils/currencyContext';
import { WebPlanContext } from '../../utils/webPlanContext';
import { CloudPlanContext } from '../../utils/cloudPlanContext';
import getCurrencySign from '../../utils/getCurrencySign';
import TableCollapse from '../tableCollapse';
import { webHostingCloud, webHostingPlans } from '../../resources/static/index';

function ExtendedPlanColumns({ period }) {
    const { currentCurrency } = useContext(CurrencyContext);
    const { currentWebPlan } = useContext(WebPlanContext);
    const { currentCloudPlan, setGlobalCloudPlan } = useContext(CloudPlanContext);
    const { i18n, t } = useTranslation();
    const [openSections, setOpenSections] = useState({});

    const currentLanguage = i18n.language;
    const isFR = currentLanguage === 'fr';

    const toggleCollapse = (id) => {
        setOpenSections((prevSections) => ({
            ...prevSections,
            [id]: !prevSections[id]
        }));
    };
    const changeCloudPlan = (isNext) => {
        setGlobalCloudPlan((prevPlan) => {
            if (isNext) {
                return (prevPlan % 3) + 1;
            }
            return ((prevPlan + 1) % 3) + 1;
        });
    };
    const getBillingCycle = (currentPlan) => {
        let currencyCode = 'annually';

        if (currentPlan === 'two') {
            currencyCode = 'biennially';
        } else if (currentPlan === 'three') {
            currencyCode = 'triennially';
        }

        return currencyCode;
    };
    const getCurrencyCode = (currency) => {
        let currencyCode = 1;

        if (currency === 'usd') {
            currencyCode = 4;
        } else if (currency === 'gbp') {
            currencyCode = 5;
        }

        return currencyCode;
    };
    const roundToTwo = (num) => parseFloat(num.toFixed(2));
    const sign = getCurrencySign(currentCurrency);
    const dataCloud = webHostingCloud[`cloud_${currentCloudPlan}`];
    const dataCurrency = webHostingPlans[currentCurrency];
    const data = dataCurrency[`year_${period}`];
    const isNotFirstPeriod = period > 1;
    const yearCoefficient = 12 * period;
    const green = '#00af60';
    const red = '#f00';

    return (
        <Row>
            <table width="100%" className="extended-plan-table" id="extended-plan-table">
                <thead>
                    <tr>
                        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                        <th />
                        <th className="plan-card">
                            <h2 className="plan-title">Solo</h2>
                            <div className="plan-price">
                                <p className="price-info">
                                    <span className="price-home">
                                        {data.solo}
                                        <sup>{sign}</sup>
                                    </span>
                                    /{t('titles.month')}
                                </p>
                                <p className="price-year">
                                    {isNotFirstPeriod && (
                                        <del>
                                            {roundToTwo(dataCurrency.year_1.solo * yearCoefficient)}
                                            {sign}
                                        </del>
                                    )}
                                    &nbsp;
                                    <strong>
                                        {isNotFirstPeriod
                                            ? roundToTwo(data.solo * yearCoefficient)
                                            : roundToTwo(
                                                  dataCurrency.year_1.solo * yearCoefficient
                                              )}
                                        {sign}
                                    </strong>
                                    &nbsp;{t('titles.for')} {period}{' '}
                                    {period > 1 ? t('titles.years') : t('titles.year')}
                                </p>
                            </div>
                            <Nav className="common-nav-button yellow">
                                <Nav.Link
                                    className="navbar-brand"
                                    href={`https://obambu.com/order/?pid=179&language=${isFR ? 'french' : 'english'}&currency=${getCurrencyCode(currentCurrency)}&billingcycle=${getBillingCycle(currentWebPlan)}`}
                                    rel="noopener noreferrer"
                                >
                                    {t('buttons.order-now')}
                                </Nav.Link>
                            </Nav>
                        </th>
                        <th className="plan-card">
                            <h2 className="plan-title">Multisite</h2>
                            <div className="plan-price">
                                <p className="price-info">
                                    <span className="price-home">
                                        {data.multisite}
                                        <sup>{sign}</sup>
                                    </span>
                                    /{t('titles.month')}
                                </p>
                                <p className="price-year">
                                    {isNotFirstPeriod && (
                                        <del>
                                            {roundToTwo(
                                                dataCurrency.year_1.multisite * yearCoefficient
                                            )}
                                            {sign}
                                        </del>
                                    )}
                                    &nbsp;
                                    <strong>
                                        {isNotFirstPeriod
                                            ? roundToTwo(data.multisite * yearCoefficient)
                                            : roundToTwo(
                                                  dataCurrency.year_1.multisite * yearCoefficient
                                              )}
                                        {sign}
                                    </strong>
                                    &nbsp;{t('titles.for')} {period}{' '}
                                    {period > 1 ? t('titles.years') : t('titles.year')}
                                </p>
                            </div>
                            <Nav className="common-nav-button yellow">
                                <Nav.Link
                                    className="navbar-brand"
                                    href={`https://obambu.com/order/?pid=180&language=${isFR ? 'french' : 'english'}&currency=${getCurrencyCode(currentCurrency)}&billingcycle=${getBillingCycle(currentWebPlan)}`}
                                    rel="noopener noreferrer"
                                >
                                    {t('buttons.order-now')}
                                </Nav.Link>
                            </Nav>
                        </th>
                        <th className="plan-card">
                            <div className="plan-title-wrapper">
                                <button
                                    id="left-arrow"
                                    className="side-button"
                                    onClick={() => changeCloudPlan()}
                                    type="button"
                                >
                                    {' '}
                                    <TfiArrowCircleLeft />
                                </button>
                                <h2 className="plan-title">
                                    Cloud <span className="cloudpro-g">{dataCloud.title}</span>
                                </h2>
                                <button
                                    className="side-button right"
                                    id="right-arrow"
                                    onClick={() => changeCloudPlan(true)}
                                    type="button"
                                >
                                    {' '}
                                    <TfiArrowCircleRight />
                                </button>
                            </div>
                            <div className="plan-price">
                                <p className="price-info">
                                    <span className="price-home">
                                        {data[`cloud_${currentCloudPlan}`]}
                                        <sup>{sign}</sup>
                                    </span>
                                    /{t('titles.month')}
                                </p>
                                <p className="price-year">
                                    {isNotFirstPeriod && (
                                        <del>
                                            {roundToTwo(
                                                dataCurrency.year_1[`cloud_${currentCloudPlan}`] *
                                                    yearCoefficient
                                            )}
                                            {sign}
                                        </del>
                                    )}
                                    &nbsp;
                                    <strong>
                                        {isNotFirstPeriod
                                            ? roundToTwo(
                                                  data[`cloud_${currentCloudPlan}`] *
                                                      yearCoefficient
                                              )
                                            : roundToTwo(
                                                  dataCurrency.year_1[`cloud_${currentCloudPlan}`] *
                                                      yearCoefficient
                                              )}
                                        {sign}
                                    </strong>
                                    &nbsp;{t('titles.for')} {period}{' '}
                                    {period > 1 ? t('titles.years') : t('titles.year')}
                                </p>
                            </div>
                            <Nav className="common-nav-button yellow">
                                <Nav.Link
                                    className="navbar-brand"
                                    href={`https://obambu.com/order/?pid=181&language=${isFR ? 'french' : 'english'}&currency=${getCurrencyCode(currentCurrency)}&billingcycle=${getBillingCycle(currentWebPlan)}`}
                                    rel="noopener noreferrer"
                                >
                                    {t('buttons.order-now')}
                                </Nav.Link>
                            </Nav>
                        </th>
                    </tr>
                </thead>
                <tbody className="card-header">
                    <tr>
                        <th colSpan="5" className="sub-heading">
                            {t('web-hosting-page.ext-plans-column.table-1.title')}
                        </th>
                    </tr>
                </tbody>
                <tbody>
                    <tr>
                        <th>{t('web-hosting-page.ext-plans-column.table-1.property-1')}</th>
                        <td>1</td>
                        <td>20</td>
                        <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                    </tr>
                    <tr>
                        <th>{t('web-hosting-page.ext-plans-column.table-1.property-2')}</th>
                        <td>50{t('web-hosting-page.ext-plans-column.gb')}</td>
                        <td>100{t('web-hosting-page.ext-plans-column.gb')}</td>
                        <td className="slider-option-space">
                            {isFR ? dataCloud.ssd_fr : dataCloud.ssd}
                        </td>
                    </tr>
                    <tr>
                        <th>{t('web-hosting-page.ext-plans-column.table-1.property-3')}</th>
                        <td>30</td>
                        <td>200</td>
                        <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                    </tr>
                    <tr>
                        <th>{t('web-hosting-page.ext-plans-column.table-1.property-4')}</th>
                        <td>10</td>
                        <td>100</td>
                        <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                    </tr>
                    <tr>
                        <th>{t('web-hosting-page.ext-plans-column.table-1.property-5')}</th>
                        <td>1 {t('web-hosting-page.ext-plans-column.cpu')}</td>
                        <td>1 {t('web-hosting-page.ext-plans-column.cpu')}</td>
                        <td className="slider-option-cpu">{dataCloud.cpu}</td>
                    </tr>
                    <tr>
                        <th>{t('web-hosting-page.ext-plans-column.table-1.property-6')}</th>
                        <td>2{t('web-hosting-page.ext-plans-column.gb')}</td>
                        <td>2{t('web-hosting-page.ext-plans-column.gb')}</td>
                        <td className="slider-option-ram">
                            {isFR ? dataCloud.ram_fr : dataCloud.ram}
                        </td>
                    </tr>
                    <tr>
                        <th>{t('web-hosting-page.ext-plans-column.table-1.property-7')}</th>
                        <td>{t('web-hosting-page.ext-plans-column.weekly')}</td>
                        <td>{t('web-hosting-page.ext-plans-column.weekly')}</td>
                        <td>{t('web-hosting-page.ext-plans-column.daily')}</td>
                    </tr>
                    <tr>
                        <th>{t('web-hosting-page.ext-plans-column.table-1.property-8')}</th>
                        <td>
                            {' '}
                            <TfiClose color={red} />
                        </td>
                        <td>
                            {' '}
                            <TfiClose color={red} />
                        </td>
                        <td>
                            {' '}
                            <TfiCheck color={green} />
                        </td>
                    </tr>
                    <tr>
                        <th>{t('web-hosting-page.ext-plans-column.table-1.property-9')}</th>
                        <td>{t('web-hosting-page.ext-plans-column.unlimited_singular')}</td>
                        <td>{t('web-hosting-page.ext-plans-column.unlimited_singular')}</td>
                        <td>{t('web-hosting-page.ext-plans-column.unlimited_singular')}</td>
                    </tr>
                    <tr>
                        <th>{t('web-hosting-page.ext-plans-column.table-1.property-10')}</th>
                        <td>
                            {' '}
                            <TfiCheck color={green} />
                        </td>
                        <td>
                            {' '}
                            <TfiCheck color={green} />
                        </td>
                        <td>
                            {' '}
                            <TfiCheck color={green} />
                        </td>
                    </tr>
                    <tr>
                        <th>{t('web-hosting-page.ext-plans-column.table-1.property-11')}</th>
                        <td>
                            {' '}
                            <TfiCheck color={green} />
                        </td>
                        <td>
                            {' '}
                            <TfiCheck color={green} />
                        </td>
                        <td>
                            {' '}
                            <TfiCheck color={green} />
                        </td>
                    </tr>
                </tbody>
            </table>
            <div className="table-wrapper">
                <TableCollapse
                    isOpen={!!openSections.section1}
                    toggle={toggleCollapse}
                    id="section1"
                    title={t('web-hosting-page.ext-plans-column.table-2.title')}
                >
                    <table className="pricing-table">
                        <tbody>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-2.property-1')}</th>
                                <td>1</td>
                                <td>20</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-2.property-2')}</th>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-2.property-3')}</th>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-2.property-4')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-2.property-5')}</th>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                            </tr>
                        </tbody>
                    </table>
                </TableCollapse>
            </div>
            <div className="table-wrapper">
                <TableCollapse
                    isOpen={!!openSections.section2}
                    toggle={toggleCollapse}
                    id="section2"
                    title={t('web-hosting-page.ext-plans-column.table-3.title')}
                >
                    <table className="pricing-table">
                        <tbody>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-3.property-1')}</th>
                                <td>30</td>
                                <td>200</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-3.property-2')}</th>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited_singular')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited_singular')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited_singular')}</td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-3.property-3')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-3.property-4')}</th>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-3.property-5')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-3.property-6')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-3.property-7')}</th>
                                <td>Outlook, Apple Mail...</td>
                                <td>Outlook, Apple Mail...</td>
                                <td>Outlook, Apple Mail...</td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-3.property-8')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-3.property-9')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>
                                    {t('web-hosting-page.ext-plans-column.table-3.property-10')}
                                </th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </TableCollapse>
            </div>
            <div className="table-wrapper">
                <TableCollapse
                    isOpen={!!openSections.section3}
                    toggle={toggleCollapse}
                    id="section3"
                    title={t('web-hosting-page.ext-plans-column.table-4.title')}
                >
                    <table className="pricing-table">
                        <tbody>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-4.property-1')}</th>
                                <td>10</td>
                                <td>100</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-4.property-2')}</th>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited_singular')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited_singular')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited_singular')}</td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-4.property-3')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-4.property-4')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </TableCollapse>
            </div>
            <div className="table-wrapper">
                <TableCollapse
                    isOpen={!!openSections.section4}
                    toggle={toggleCollapse}
                    id="section4"
                    title={t('web-hosting-page.ext-plans-column.table-5.title')}
                >
                    <table className="pricing-table">
                        <tbody>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-5.property-1')}</th>
                                <td>50{t('web-hosting-page.ext-plans-column.gb')}</td>
                                <td>100{t('web-hosting-page.ext-plans-column.gb')}</td>
                                <td className="slider-option-space">
                                    {isFR ? dataCloud.ssd_fr : dataCloud.ssd}
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-5.property-2')}</th>
                                <td>{t('web-hosting-page.ext-plans-column.weekly')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.weekly')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.daily')}</td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-5.property-3')}</th>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-5.property-4')}</th>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited_singular')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited_singular')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited_singular')}</td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-5.property-5')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-5.property-6')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-5.property-7')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-5.property-8')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-5.property-9')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </TableCollapse>
            </div>
            <div className="table-wrapper">
                <TableCollapse
                    isOpen={!!openSections.section5}
                    toggle={toggleCollapse}
                    id="section5"
                    title={t('web-hosting-page.ext-plans-column.table-6.title')}
                >
                    <table className="pricing-table">
                        <tbody>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-6.property-1')}</th>
                                <td>1 {t('web-hosting-page.ext-plans-column.cpu')}</td>
                                <td>1 {t('web-hosting-page.ext-plans-column.cpu')}</td>
                                <td className="slider-option-cpu">{dataCloud.cpu}</td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-6.property-2')}</th>
                                <td>1{t('web-hosting-page.ext-plans-column.gb')}</td>
                                <td>1{t('web-hosting-page.ext-plans-column.gb')}</td>
                                <td className="slider-option-ram">
                                    {isFR ? dataCloud.ram_fr : dataCloud.ram}
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-6.property-3')}</th>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited_singular')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited_singular')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited_singular')}</td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-6.property-4')}</th>
                                <td>1024</td>
                                <td>1024</td>
                                <td className="slider-option-iops">{dataCloud.cpu_operations}</td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-6.property-5')}</th>
                                <td>25</td>
                                <td>25</td>
                                <td className="slider-option-ep">{dataCloud.entry_process}</td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-6.property-6')}</th>
                                <td>100</td>
                                <td>100</td>
                                <td className="slider-option-nproc">{dataCloud.total_processes}</td>
                            </tr>
                        </tbody>
                    </table>
                </TableCollapse>
            </div>
            <div className="table-wrapper">
                <TableCollapse
                    isOpen={!!openSections.section6}
                    toggle={toggleCollapse}
                    id="section6"
                    title={t('web-hosting-page.ext-plans-column.table-7.title')}
                >
                    <table className="pricing-table">
                        <tbody>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-7.property-1')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-7.property-2')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-7.property-3')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-7.property-4')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-7.property-5')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-7.property-6')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-7.property-7')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-7.property-8')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-7.property-9')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>
                                    {t('web-hosting-page.ext-plans-column.table-7.property-10')}
                                </th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </TableCollapse>
            </div>
            <div className="table-wrapper">
                <TableCollapse
                    isOpen={!!openSections.section7}
                    toggle={toggleCollapse}
                    id="section7"
                    title={t('web-hosting-page.ext-plans-column.table-8.title')}
                >
                    <table className="pricing-table">
                        <tbody>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-8.property-1')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-8.property-2')}</th>
                                <td>+300</td>
                                <td>+300</td>
                                <td>+300</td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-8.property-3')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-8.property-4')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-8.property-5')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-8.property-6')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-8.property-7')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-8.property-8')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-8.property-9')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>
                                    {t('web-hosting-page.ext-plans-column.table-8.property-10')}
                                </th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>
                                    {t('web-hosting-page.ext-plans-column.table-8.property-11')}
                                </th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </TableCollapse>
            </div>
            <div className="table-wrapper">
                <TableCollapse
                    isOpen={!!openSections.section8}
                    toggle={toggleCollapse}
                    id="section8"
                    title={t('web-hosting-page.ext-plans-column.table-9.title')}
                >
                    <table className="pricing-table">
                        <tbody>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-9.property-1')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-9.property-2')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-9.property-3')}</th>
                                <td>
                                    {' '}
                                    <TfiClose color={red} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiClose color={red} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-9.property-4')}</th>
                                <td>
                                    {' '}
                                    <TfiClose color={red} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiClose color={red} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-9.property-5')}</th>
                                <td>
                                    {' '}
                                    <TfiClose color={red} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiClose color={red} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-9.property-6')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-9.property-7')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('web-hosting-page.ext-plans-column.table-9.property-8')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </TableCollapse>
            </div>
            <div className="table-wrapper">
                <TableCollapse
                    isOpen={!!openSections.section9}
                    toggle={toggleCollapse}
                    id="section9"
                    title={t('web-hosting-page.ext-plans-column.table-10.title')}
                >
                    <table className="pricing-table rowthree">
                        <tbody>
                            <tr>
                                <th>
                                    {t('web-hosting-page.ext-plans-column.table-10.property-1')}
                                </th>
                                <td>{t('web-hosting-page.ext-plans-column.included')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.included')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.included')}</td>
                            </tr>
                            <tr>
                                <th>
                                    {t('web-hosting-page.ext-plans-column.table-10.property-2')}
                                </th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>
                                    {t('web-hosting-page.ext-plans-column.table-10.property-3')}
                                </th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>
                                    {t('web-hosting-page.ext-plans-column.table-10.property-4')}
                                </th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>
                                    {t('web-hosting-page.ext-plans-column.table-10.property-5')}
                                </th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>
                                    {t('web-hosting-page.ext-plans-column.table-10.property-6')}
                                </th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </TableCollapse>
            </div>
            <div className="table-wrapper">
                <TableCollapse
                    isOpen={!!openSections.section10}
                    toggle={toggleCollapse}
                    id="section10"
                    title={t('web-hosting-page.ext-plans-column.table-11.title')}
                >
                    <table className="pricing-table rowthree">
                        <tbody>
                            <tr>
                                <th>
                                    {t('web-hosting-page.ext-plans-column.table-11.property-1')}
                                </th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>
                                    {t('web-hosting-page.ext-plans-column.table-11.property-2')}
                                </th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>
                                    {t('web-hosting-page.ext-plans-column.table-11.property-3')}
                                </th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>
                                    {t('web-hosting-page.ext-plans-column.table-11.property-4')}
                                </th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>
                                    {t('web-hosting-page.ext-plans-column.table-11.property-5')}
                                </th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>
                                    {t('web-hosting-page.ext-plans-column.table-11.property-6')}
                                </th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </TableCollapse>
            </div>
        </Row>
    );
}

ExtendedPlanColumns.propTypes = {
    period: PropTypes.number
};

ExtendedPlanColumns.defaultProps = {
    period: 1
};

export default ExtendedPlanColumns;
